import { Document, Page, pdf, Text, View } from "@react-pdf/renderer"
import { styles, TableCell } from "components"
import moment from "moment"
import { cellValuePdf, RowExportTypePdf } from "types"
import { convertNumber } from "utils"
import { AgedType } from "../../types"
import { calculateSummary } from "../functions"

type UseExportPdfAgedReceiveable = {
  data: AgedType[]
  dateFilter: string
}

export const useExportPdfAgedReceiveable = ({
  data,
  dateFilter
}: UseExportPdfAgedReceiveable) => {

  const columns = [
    { header: "NO", key: "no", width: 5 },
    { header: "INVOICE ID#", key: "invoice_id", width: 25 },
    { header: "REF SO ID#", key: "ref_so_id", width: 25 },
    { header: "DATE", key: "date", width: 15 },
    { header: "CUSTOMER", key: "customer", width: 15 },
    { header: "BALANCE", key: "balance", width: 15 },
    { header: "7 DAYS", key: "7days", width: 15 },
    { header: "15 DAYS", key: "15days", width: 15 },
    { header: "21 DAYS", key: "21days", width: 15 },
    { header: "30 DAYS", key: "30days", width: 15 },
    { header: "60+ DAYS", key: "60days", width: 15 },
  ]

  function numberFormat(value: number): cellValuePdf {
    return {
      text: convertNumber(value).intoCurrency,
      style: {
        textAlign: "right",
        fontWeight: "bold"
      }
    }
  }

  const calculate = calculateSummary(data)

  const dataMap: RowExportTypePdf[] = data.map((item, index) => {
    // Vars
    const total = numberFormat(item.total_amount)
    const balance = numberFormat(item.balance)

    return {
      no: (index + 1).toString(),
      invoice_id: item.trx_code,
      ref_so_id: item.trx_code_so,
      date: moment(item.transaction_date).format("DD/MM/YYYY"),
      customer: item.customer.customer_name,
      balance: total,
      "7days": item.aged_receivable <= 7 ? balance : "",
      "15days": item.aged_receivable > 7 && item.aged_receivable <= 15 ? balance : "",
      "21days": item.aged_receivable > 15 && item.aged_receivable <= 21 ? balance : "",
      "30days": item.aged_receivable > 21 && item.aged_receivable <= 30 ? balance : "",
      "60days": item.aged_receivable > 30 ? balance : "",
    }
  })

  return {
    handleExportPdf: () =>
      pdf(
        <PdfTemplate
          data={dataMap}
          column={columns}
          date={dateFilter}
          calculate={calculate}
        />
      )
        .toBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement("a")
          link.href = url
          link.download = `aged-receiveable-${dateFilter}.pdf`
          link.click()
          URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.error("Gagal membuat PDF:", error)
        })
  }
}

function PdfTemplate({
  data,
  column,
  date,
  calculate
}: {
  data: RowExportTypePdf[]
  column: { header: string; key: string; width: number }[]
  date?: string
  calculate: ReturnType<typeof calculateSummary>
}) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>SALES</Text>
        <Text style={styles.title}>AGED RECEIVABLE</Text>
        {date && <Text style={styles.date}>Date: {date}</Text>}
        <TableCell data={data} column={column} />
        <View
          style={{
            marginTop: 10,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-end"
          }}>
          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY TOTAL : </Text>

            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate.balance).intoCurrency}
            </Text>
          </View>
          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY 7 DAYS : </Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate["7_day"]).intoCurrency}
            </Text>
          </View>

          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY 15 DAYS : </Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate["15_day"]).intoCurrency}
            </Text>
          </View>

          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY 21 DAYS : </Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate["21_day"]).intoCurrency}
            </Text>
          </View>

          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY 30 DAYS : </Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate["30_day"]).intoCurrency}
            </Text>
          </View>

          <View
            style={[
              styles.date,
              {
                display: "flex",
                flexDirection: "row",
                fontSize: 8
              }
            ]}>
            <Text>SUMMARY 60+ DAYS : </Text>
            <Text style={{ width: 50, textAlign: "right", paddingLeft: 2 }}>
              {convertNumber(calculate["60_day"]).intoCurrency}
            </Text>
          </View>
        </View>
      </Page>

    </Document>
  )
}
